import { createFileRoute, Link } from "@tanstack/react-router";
import { btn } from "@/components/ds/styles";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { publicSiteSettingsQuery } from "@/routes/_public/route";
import { toWhatsAppLink } from "@/lib/whatsapp-link";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Check, Hourglass, MessageCircle } from "lucide-react";

const search = z.object({
  product: z.enum(["v1", "smart"]).optional(),
  rail: z.enum(["paypal", "local"]).optional(),
});

export const Route = createFileRoute("/_public/bousala/pending")({
  validateSearch: search,
  head: () => ({
    meta: [
      { title: "وصلنا طلبك — قيد المراجعة / Under review" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PendingPage,
});

function PendingPage() {
  const { product, rail } = Route.useSearch();
  const smart = product === "smart";
  const paypal = rail === "paypal";
  const [lang, setLang] = useState<"ar" | "en">("ar");
  const [signedIn, setSignedIn] = useState(false);
  const { data: settings } = useQuery(publicSiteSettingsQuery());
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("bousala.lang");
      if (stored === "en") setLang("en");
      else setLang("ar");
    } catch {}
    void supabase.auth.getSession().then(({ data }) => {
      setSignedIn(Boolean(data.session && !data.session.user.is_anonymous));
    });
  }, []);
  const en = lang === "en";
  const dir = en ? "ltr" : "rtl";
  const t = en
    ? {
        eyebrow: "Your order",
        title: paypal ? "We received your PayPal proof" : "We received your payment proof",
        body: paypal
          ? "We'll review the PayPal payment within 24 hours and message you on WhatsApp."
          : smart
            ? "We'll review the transfer within 24 hours and message you on WhatsApp with a link to the preferences form for Smart Bousala."
            : "We'll review the transfer within 24 hours and message you on WhatsApp with an activation link so you can start the test.",
        chip: "We'll send the link via WhatsApp to the number you entered",
        footer: "For any question, reach us directly from the contact page.",
        next: "What happens now?",
        s1: "Your order and proof arrived.",
        s2: "Manual review of the payment.",
        s3: "A WhatsApp message to the number you entered.",
        wa: "WhatsApp us",
        primary: signedIn ? "My portal →" : "Create an account to track the order →",
      }
    : {
        eyebrow: "الطلب",
        title: "وصلنا طلبك.",
        body: paypal
          ? "وصلنا إثبات الدفع عبر PayPal — منراجع الدفعة خلال 24 ساعة ومنتواصل معك على واتساب."
          : smart
            ? "منراجع الحوالة خلال 24 ساعة، ومنتواصل معك على واتساب برابط نموذج التفضيلات الخاص بالبوصلة الذكية."
            : "منراجع الحوالة خلال 24 ساعة، ومنتواصل معك على واتساب برابط تفعيل الحساب للبدء بالاختبار.",
        chip: "الرابط بيوصل على واتساب، على الرقم اللي انكتب بالطلب",
        footer: "",
        next: "شو بيصير هلّق؟",
        s1: "الطلب وإثبات الدفع وصلوا.",
        s2: "المراجعة اليدوية للدفع.",
        s3: "رسالة على واتساب على الرقم اللي انكتب بالطلب.",
        wa: "التواصل على واتساب",
        primary: signedIn ? "لوحتي ←" : "إنشاء حساب لمتابعة الطلب ←",
      };
  const wa = settings?.clinic_whatsapp
    ? toWhatsAppLink(settings.clinic_whatsapp, en ? "Hello, I have a question about my Bousala order" : "مرحباً، عندي سؤال عن طلب البوصلة")
    : null;
  return (
    <div dir={dir} className="mx-auto max-w-[44rem] px-5 py-10 md:py-14">
      <span className="grid h-16 w-16 place-items-center rounded-ds-lg bg-amber-100 text-amber-800">
        <Hourglass className="h-8 w-8" strokeWidth={1.75} aria-hidden />
      </span>
      <p className="mt-5 font-display text-[14px] font-bold text-amber-800">{t.eyebrow}</p>
      <h1 className="mt-2 font-display text-[34px] font-extrabold leading-tight text-brown-700">{t.title}</h1>
      <p className="mt-4 text-[17px] leading-[1.85] text-brown-700">{t.body}</p>

      <h2 className="mt-10 font-display text-[23px] font-extrabold text-brown-700">{t.next}</h2>
      <ol className="mt-3 divide-y divide-hairline-subtle">
        <li className="flex items-center gap-4 py-4">
          <span className="grid h-10 w-10 flex-none place-items-center rounded-ds-md bg-green-100 text-green-800">
            <Check className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          </span>
          <span className="text-[17px] text-brown-700">{t.s1}</span>
        </li>
        <li className="flex items-center gap-4 py-4">
          <span className="w-10 flex-none text-center font-display text-[26px] font-extrabold text-amber-800">02</span>
          <span className="text-[17px] text-brown-700">{t.s2}</span>
        </li>
        <li className="flex items-center gap-4 py-4">
          <span className="w-10 flex-none text-center font-display text-[26px] font-extrabold text-amber-800">03</span>
          <span className="text-[17px] text-brown-700">{t.s3}</span>
        </li>
      </ol>

      <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        {signedIn ? (
          <Link to="/portal" className={PRIMARY}>{t.primary}</Link>
        ) : (
          <Link to="/auth" search={{ mode: "sign_up", redirect: "/portal" } as any} className={PRIMARY}>{t.primary}</Link>
        )}
        {wa ? (
          <a href={wa} target="_blank" rel="noreferrer" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-ds-pill border border-hairline-default px-5 font-display text-[15px] font-bold text-brown-700 hover:bg-cream-100">
            <MessageCircle className="h-5 w-5" strokeWidth={1.75} aria-hidden />
            {t.wa}
          </a>
        ) : null}
      </div>
      {t.footer ? <p className="mt-6 text-[14px] text-brown-500">{t.footer}</p> : null}
    </div>
  );
}

const PRIMARY =
  `${btn.primary} min-h-14`;
